import type {
    ToolStepDirectionalEnvelope,
    ToolStepRingLayout,
} from '@/workflow/_contract/geometry';

function assertFiniteNonNegative(name: string, value: number): void {
    if (!Number.isFinite(value) || value < 0) {
        throw new Error(`${name} must be finite and non-negative.`);
    }
}

export function deriveUniformToolStepRingLayout(args: {
    stepCount: number;
    startRadius: number;
    minimumRingGap: number;
    minimumInnerBoundaryRadius?: number;
    envelopeByStepIndex: readonly ToolStepDirectionalEnvelope[];
}): ToolStepRingLayout {
    if (!Number.isInteger(args.stepCount) || args.stepCount < 0) {
        throw new Error('ToolStep count must be a non-negative integer.');
    }
    if (args.envelopeByStepIndex.length !== args.stepCount) {
        throw new Error('ToolStep envelope count must match ToolStep count.');
    }
    assertFiniteNonNegative('ToolStep start radius', args.startRadius);
    assertFiniteNonNegative('ToolStep minimum ring gap', args.minimumRingGap);
    const minimumInnerBoundaryRadius = args.minimumInnerBoundaryRadius ?? 0;
    assertFiniteNonNegative('ToolStep minimum inner boundary radius', minimumInnerBoundaryRadius);

    let maximumDirectionalExtent = 0;
    let maximumInwardExtent = 0;
    for (const [index, envelope] of args.envelopeByStepIndex.entries()) {
        assertFiniteNonNegative(`ToolStep ${index} inward envelope`, envelope.inward);
        assertFiniteNonNegative(`ToolStep ${index} outward envelope`, envelope.outward);
        const axialHalfThickness = envelope.axialHalfThickness ?? 0;
        const perpendicularRadius = envelope.perpendicularRadius ?? 0;
        assertFiniteNonNegative(`ToolStep ${index} axial half-thickness`, axialHalfThickness);
        assertFiniteNonNegative(`ToolStep ${index} perpendicular radius`, perpendicularRadius);
        const inwardExtent = envelope.inward + axialHalfThickness;
        maximumInwardExtent = Math.max(maximumInwardExtent, inwardExtent);
        maximumDirectionalExtent = Math.max(maximumDirectionalExtent, inwardExtent);
    }

    const startRadius = Math.max(
        args.startRadius,
        minimumInnerBoundaryRadius + maximumInwardExtent,
    );
    for (const envelope of args.envelopeByStepIndex) {
        const axialHalfThickness = envelope.axialHalfThickness ?? 0;
        const perpendicularRadius = envelope.perpendicularRadius ?? 0;
        const outwardCenterRadius = startRadius + envelope.outward + axialHalfThickness;
        const cylindricalOutwardExtent = Math.hypot(outwardCenterRadius, perpendicularRadius)
            - startRadius;
        maximumDirectionalExtent = Math.max(maximumDirectionalExtent, cylindricalOutwardExtent);
    }

    // Half of the clearance belongs on each side of the midpoint boundary.
    const ringGap = Math.max(
        args.minimumRingGap,
        2 * maximumDirectionalExtent,
    );
    const ringRadii = Array.from(
        { length: args.stepCount },
        (_, index) => startRadius + (index * ringGap),
    );

    return {
        startRadius,
        ringGap,
        ringRadii,
    };
}